import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { useFlags } from 'launchdarkly-react-client-sdk'
import Image from 'next/image'
import { FC, Fragment, useCallback, useState } from 'react'
import { isNotEmpty } from 'ramda'

import { Icon } from '~/src/components/generic/PhosphorIcons/index'

import LightCircleQuestion from '~/src/components/shared/NavBar/img/LightCircleQuestion.svg'
import SidePanel from '~/src/components/shared/NavBar/components/SidePanel/index'

import { isRadiusUser } from '~/helpers/auth'

import { useAuthenticatedUser } from '~/hooks/useAuthentication'
import useTrackCvtEvent from '~/src/hooks/useTrackCvtEvent'
import useTranslation from '~/src/hooks/useTranslation'

import TrainingRequest from '~/src/components/TrainingRequest'
import colors from '~/config/colorsScheme'
import { FeatureFlag, useFeatureFlagEnabled } from '~/lib/featureFlags'
import { MixpanelEvent, track } from '~/clients/mixpanelClient'

type Props = {
  onProvideFeedbackClicked: () => void
}

const SupportMenu: FC<Props> = ({ onProvideFeedbackClicked }) => {
  const [isOpenUserGuide, setIsOpenUserGuide] = useState(false)
  const [isTrainingRequestOpen, setIsTrainingRequestOpen] = useState(false)
  const [user] = useAuthenticatedUser()
  const { t } = useTranslation('common')
  const trackCvtEvent = useTrackCvtEvent()

  const useCvtModalForm = !isRadiusUser(user)

  const { radiusUserGuide } = useFlags()
  const isRequestTrainingEnabled = useFeatureFlagEnabled(
    FeatureFlag.EnableRequestTraining
  )

  const handleOpenMenu = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        trackCvtEvent(MixpanelEvent.CVTHelpMenuOpened)
      }
    },
    [trackCvtEvent]
  )

  const handleRequestHelp = useCallback(() => {
    trackCvtEvent(MixpanelEvent.CVTContactSupportClicked)

    if (useCvtModalForm) {
      window.open(process.env.NEXT_PUBLIC_SERVICENOW_CVT_URL, '_blank')
    } else {
      window.open(process.env.NEXT_PUBLIC_SERVICENOW_RADIUS_URL, '_blank')
    }
  }, [useCvtModalForm, trackCvtEvent])

  const handleRequestTraining = useCallback(() => {
    trackCvtEvent(MixpanelEvent.CVTRequestTrainingClicked)
    setIsTrainingRequestOpen(true)
  }, [trackCvtEvent])

  const handleProvideFeedback = useCallback(() => {
    trackCvtEvent(MixpanelEvent.CVTProvideFeedbackClicked)
    onProvideFeedbackClicked()
  }, [trackCvtEvent, onProvideFeedbackClicked])

  const handleUserGuide = useCallback(() => {
    if (!isOpenUserGuide) {
      track(MixpanelEvent.RadiusUserGuideViewed)
    }

    setIsOpenUserGuide((prev) => !prev)
  }, [isOpenUserGuide])

  return (
    <>
      <Menu>
        {({ open }) => (
          <>
            <MenuButton
              className="rounded p-2 hover:bg-gray-100"
              onClick={() => handleOpenMenu(open)}
            >
              <div className="relative">
                <Image
                  alt="Question mark in circle icon"
                  height={24}
                  src={LightCircleQuestion}
                  width={24}
                />
              </div>
            </MenuButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems
                anchor="bottom end"
                className="z-50 flex w-60 flex-col items-start rounded bg-white shadow-full *:p-3"
              >
                <MenuItem key="item1">
                  <button
                    className="flex w-full flex-row items-center space-x-4 data-[focus]:bg-gray-100"
                    onClick={handleRequestHelp}
                  >
                    <Icon color={colors.gray[900]} name="QuestionIcon" size="22" />
                    <div>{t('nav.contactSupport')}</div>
                  </button>
                </MenuItem>
                {isRequestTrainingEnabled && !isRadiusUser(user) && (
                  <MenuItem key="requestTraining">
                    <button
                      className="flex w-full flex-row items-center space-x-4 data-[focus]:bg-gray-100"
                      onClick={handleRequestTraining}
                    >
                      <Icon
                        color={colors.gray[900]}
                        name="BookOpenTextIcon"
                        size="22"
                      />
                      <div>{t('nav.requestTraining')}</div>
                    </button>
                  </MenuItem>
                )}
                <MenuItem key="item2">
                  <button
                    className="flex w-full flex-row items-center space-x-4 data-[focus]:bg-gray-100"
                    onClick={handleProvideFeedback}
                  >
                    <Icon color={colors.gray[900]} name="NotePencilIcon" size="22" />
                    <div>{t('nav.provideFeedback')}</div>
                  </button>
                </MenuItem>
                {isNotEmpty(radiusUserGuide) && isRadiusUser(user) && (
                  <MenuItem key="item3">
                    <button
                      className="flex w-full flex-row items-center space-x-4 data-[focus]:bg-gray-100"
                      onClick={handleUserGuide}
                    >
                      <Icon color={colors.gray[900]} name="InfoIcon" size="22" />
                      <div>{t('nav.userGuide')}</div>
                    </button>
                  </MenuItem>
                )}
              </MenuItems>
            </Transition>
          </>
        )}
      </Menu>

      <SidePanel
        guideUrl={radiusUserGuide}
        isOpen={isOpenUserGuide}
        onClose={handleUserGuide}
      />

      {isTrainingRequestOpen && (
        <TrainingRequest
          isOpen={isTrainingRequestOpen}
          onCloseHandler={() => setIsTrainingRequestOpen(false)}
        />
      )}
    </>
  )
}

export default SupportMenu
